import { useQuery } from '@tanstack/react-query';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';

import { assetQueries } from '@/lib/queries/assets';

/**
 * The WYSIWYG payoff (Spec 39 §3b): the author sees the actual image and caption
 * in the editor, not a placeholder. The node stores only an assetId, so the view
 * fetches its own asset (React-Query cached → one lookup per distinct id across
 * all instances). Atom node — TipTap handles selection/delete natively; the ring
 * marks the selected figure.
 */
export function ImageNodeView({ node, selected }: NodeViewProps) {
  const assetId = (node.attrs.assetId as string | null) ?? undefined;
  const { data: asset } = useQuery(assetQueries.byId(assetId));

  return (
    <NodeViewWrapper
      className={`my-4 rounded-md ${selected ? 'ring-2 ring-oxblood ring-offset-2' : ''}`}
      data-drag-handle
    >
      {asset ? (
        <figure className="text-center">
          <img
            src={asset.publicUrl}
            alt={asset.altText}
            className="mx-auto max-w-full rounded-md"
          />
          {asset.caption && (
            <figcaption className="mt-2 font-sans text-sm text-ink/60">{asset.caption}</figcaption>
          )}
        </figure>
      ) : (
        <div className="rounded-md border border-dashed border-ink/20 p-6 text-center font-sans text-sm text-ink/50">
          {assetId ? 'Loading image…' : 'Image unavailable'}
        </div>
      )}
    </NodeViewWrapper>
  );
}
